/**
 * Admin — Notizie Assicurative Workflow (feb-2026 Step 5)
 *
 * Pagina staff-only per il workflow editoriale delle notizie assicurative.
 * Backend: /api/admin/notizie/*  (routes/admin_notizie.py)
 *
 * Tabs:
 *   - "Da approvare"     (in_review)  ← default all'apertura
 *   - "Pubblicate oggi"  (published, published_at = oggi)
 *   - "Bozze / Rigettate" (draft, rejected)
 *   - "Ritirate"          (withdrawn)
 *   - "Storico compliance" (withdrawn con reason inizia con auto_compliance)
 *   - "Tutti gli articoli"
 *
 * Azioni: approva, rigetta, edita testo_finale, ritira, bulk-approve.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Search,
  Newspaper, Clock, ShieldCheck, ShieldAlert, Edit3, Undo2, Send, ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATO_BADGE = {
  in_review:  "bg-amber-100 text-amber-900 border-amber-300",
  published:  "bg-emerald-100 text-emerald-900 border-emerald-300",
  draft:      "bg-neutral-100 text-neutral-800 border-neutral-300",
  rejected:   "bg-rose-100 text-rose-900 border-rose-300",
  withdrawn:  "bg-violet-100 text-violet-900 border-violet-300",
};

function timeAgo(iso) {
  if (!iso) return "-";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "-";
  const diff = Math.round((Date.now() - t) / 1000);
  if (diff < 60)      return `${diff}s fa`;
  if (diff < 3600)    return `${Math.round(diff / 60)}m fa`;
  if (diff < 86400)   return `${Math.round(diff / 3600)}h fa`;
  return `${Math.round(diff / 86400)}g fa`;
}

function ComplianceBadge({ result }) {
  const passed = result.passed;
  const sev = result.severity;
  const cls = passed
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : sev === "block"
      ? "bg-rose-50 text-rose-900 border-rose-300"
      : sev === "warn"
        ? "bg-amber-50 text-amber-900 border-amber-300"
        : "bg-neutral-50 text-neutral-700 border-neutral-200";
  const Icon = passed
    ? CheckCircle2
    : sev === "block" ? XCircle : AlertTriangle;
  return (
    <div className={`border rounded-lg p-2 flex items-start gap-2 ${cls}`} data-testid={`compliance-${result.check_name}`}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
      <div className="text-xs flex-1">
        <div className="font-semibold">{result.check_name}</div>
        <div className="opacity-80 mt-0.5 break-words">{result.details}</div>
      </div>
    </div>
  );
}

export default function AdminNotiziePage() {
  const [tab,     setTab]     = useState("in_review");
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [detail, setDetail] = useState(null); // articolo aperto in modale
  const [editText, setEditText] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [withdrawReason, setWithdrawReason] = useState("");
  const [rerunResult, setRerunResult] = useState(null);

  const fetchItems = useCallback(async (statoTab) => {
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const params = {};
      if (statoTab === "in_review")     params.stato = "in_review";
      else if (statoTab === "published") params.stato = "published";
      else if (statoTab === "draft")     params.stato = "draft";
      else if (statoTab === "rejected")  params.stato = "rejected";
      else if (statoTab === "withdrawn") params.stato = "withdrawn";
      else if (statoTab === "compliance_history") params.stato = "withdrawn";
      params.limit = 100;
      const { data } = await axios.get(`${API}/admin/notizie/list`, { params, withCredentials: true });
      let list = data.items || [];
      if (statoTab === "published") {
        const today = new Date().toISOString().slice(0, 10);
        list = list.filter(n => (n.published_at || "").startsWith(today));
      } else if (statoTab === "compliance_history") {
        list = list.filter(n => (n.withdrawn_reason || "").startsWith("auto_compliance"));
      }
      setItems(list);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento articoli");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notizie/pending-count`, { withCredentials: true });
      setPendingCount(data.in_review || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchItems(tab); }, [tab, fetchItems]);
  useEffect(() => {
    fetchPendingCount();
    const iv = setInterval(fetchPendingCount, 60_000);
    return () => clearInterval(iv);
  }, [fetchPendingCount]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(n =>
      (n.titolo_redazionale || "").toLowerCase().includes(q) ||
      (n.titolo_originale || "").toLowerCase().includes(q) ||
      (n.fonti?.[0]?.testata || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const openDetail = (article) => {
    setDetail(article);
    setEditText(article.testo_finale || "");
    setRejectReason("");
    setWithdrawReason("");
  };

  const closeDetail = () => {
    setDetail(null);
    setEditText("");
    setRejectReason("");
    setWithdrawReason("");
  };

  const doApprove = async (id) => {
    try {
      await axios.post(`${API}/admin/notizie/${id}/approve`, {}, { withCredentials: true });
      toast.success("Articolo approvato e pubblicato");
      closeDetail();
      fetchItems(tab);
      fetchPendingCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore approvazione");
    }
  };

  const doReject = async (id) => {
    if (!rejectReason.trim() || rejectReason.trim().length < 3) {
      toast.error("Motivo del rifiuto obbligatorio (min 3 caratteri)");
      return;
    }
    try {
      await axios.post(`${API}/admin/notizie/${id}/reject`, { reason: rejectReason.trim() }, { withCredentials: true });
      toast.success("Articolo rigettato");
      closeDetail();
      fetchItems(tab);
      fetchPendingCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rifiuto");
    }
  };

  const doEdit = async (id) => {
    if (!editText || editText.trim().length < 10) {
      toast.error("Testo finale troppo corto (min 10 caratteri)");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/notizie/${id}/edit`,
        { testo_finale: editText.trim() },
        { withCredentials: true },
      );
      toast.success("Modifica salvata (verrà applicata alla prossima approvazione)");
      fetchItems(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const doWithdraw = async (id) => {
    if (!withdrawReason.trim() || withdrawReason.trim().length < 3) {
      toast.error("Motivo del ritiro obbligatorio");
      return;
    }
    try {
      await axios.post(`${API}/admin/notizie/${id}/withdraw`, { reason: withdrawReason.trim() }, { withCredentials: true });
      toast.success("Articolo ritirato");
      closeDetail();
      fetchItems(tab);
      fetchPendingCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore ritiro");
    }
  };

  const doBulkApprove = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("Seleziona almeno un articolo");
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/admin/notizie/bulk-approve`,
        { ids },
        { withCredentials: true },
      );
      const approved = data.count_approved || 0;
      const skipped = (data.skipped || []).length;
      toast.success(`${approved} approvati, ${skipped} saltati per compliance`);
      if (skipped > 0) {
        console.warn("[BULK-APPROVE] skipped:", data.skipped);
      }
      setSelectedIds(new Set());
      fetchItems(tab);
      fetchPendingCount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore bulk approve");
    }
  };

  const doRerunCompliance = async (dryRun) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/notizie/rerun-compliance`,
        { dry_run: dryRun, limit: 200, only_states: ["published"] },
        { withCredentials: true },
      );
      setRerunResult(data);
      const msg = dryRun
        ? `Dry-run: ${data.count_scanned} scansionati, ${data.count_withdrawn === 0 ? 0 : data.scanned.filter(s => s.blocked).length} bloccherebbero`
        : `Real run: ${data.count_scanned} scansionati, ${data.count_withdrawn} auto-ritirati`;
      toast.success(msg);
      if (!dryRun) fetchItems(tab);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rerun compliance");
    }
  };

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(n => n.id)));
    }
  };

  const complianceBlocked = detail?.compliance_checks?.results?.some(
    r => !r.passed && r.severity === "block"
  );

  return (
    <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="admin-notizie-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-8">
        <Link to="/admin/cockpit" className="text-sm text-[#4A5E51] hover:text-[#163321] inline-flex items-center gap-1 mb-4" data-testid="notizie-admin-back">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Torna al Cockpit
        </Link>

        <header className="mb-6 flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#163321]/10 rounded-2xl flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-[#163321]" aria-hidden="true" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-[#0F1F14]">Workflow Notizie Assicurative</h1>
              <p className="text-sm text-[#4A5E51]">Revisione editoriale IVASS · pipeline pre-pubblicazione</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => fetchItems(tab)}
              disabled={loading}
              data-testid="notizie-admin-refresh"
              aria-label="Ricarica lista articoli"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Ricarica
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => doRerunCompliance(true)}
              data-testid="notizie-admin-rerun-dryrun"
              aria-label="Rerun compliance in modalità dry-run"
            >
              <ShieldCheck className="w-4 h-4 mr-1" /> Rerun compliance (dry-run)
            </Button>
            <Button
              variant="default" size="sm" className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => {
                if (window.confirm("Vuoi eseguire il rerun compliance sui published articles?\n\nGli articoli che falliscono i check ora bloccanti verranno automaticamente ritirati.")) {
                  doRerunCompliance(false);
                }
              }}
              data-testid="notizie-admin-rerun-real"
              aria-label="Rerun compliance e ritira articoli non conformi"
            >
              <ShieldAlert className="w-4 h-4 mr-1" /> Rerun & auto-withdraw
            </Button>
          </div>
        </header>

        {rerunResult && (
          <div className="mb-4 border border-neutral-200 rounded-xl p-4 bg-white" role="status" aria-live="polite" data-testid="rerun-result-panel">
            <p className="text-sm font-semibold text-[#0F1F14] mb-1">
              Ultimo rerun compliance ({rerunResult.dry_run ? "dry-run" : "real run"})
            </p>
            <p className="text-xs text-[#4A5E51]">
              Scansionati: <b>{rerunResult.count_scanned}</b> ·
              Passati: <b>{rerunResult.count_passed}</b> ·
              Ritirati: <b>{rerunResult.count_withdrawn}</b> ·
              Errori: <b>{rerunResult.count_errors}</b> ·
              Prompt version: <code>{rerunResult.prompt_version}</code>
            </p>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-4 flex-wrap h-auto" data-testid="notizie-admin-tabs">
            <TabsTrigger value="in_review" data-testid="tab-in-review">
              Da approvare
              {pendingCount > 0 && (
                <Badge className="ml-2 bg-amber-500 text-white" data-testid="tab-in-review-badge">{pendingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="published" data-testid="tab-published-today">Pubblicate oggi</TabsTrigger>
            <TabsTrigger value="draft" data-testid="tab-draft">Bozze</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rigettate</TabsTrigger>
            <TabsTrigger value="withdrawn" data-testid="tab-withdrawn">Ritirate</TabsTrigger>
            <TabsTrigger value="compliance_history" data-testid="tab-compliance-history">Storico compliance</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">Tutti</TabsTrigger>
          </TabsList>

          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per titolo o testata…"
                className="pl-9"
                aria-label="Cerca articoli per titolo o testata"
                data-testid="notizie-admin-search"
              />
            </div>
            {tab === "in_review" && filtered.length > 0 && (
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={toggleSelectAll}
                  data-testid="notizie-admin-select-all"
                >
                  {selectedIds.size === filtered.length ? "Deseleziona tutti" : "Seleziona tutti"}
                </Button>
                <Button
                  size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={selectedIds.size === 0}
                  onClick={doBulkApprove}
                  data-testid="notizie-admin-bulk-approve"
                  aria-label={`Approva ${selectedIds.size} articoli selezionati`}
                >
                  <Send className="w-4 h-4 mr-1" /> Approva selezionati ({selectedIds.size})
                </Button>
              </>
            )}
          </div>

          <TabsContent value={tab} className="mt-0">
            {loading && (
              <div className="py-16 text-center text-sm text-neutral-500" data-testid="notizie-admin-loading">
                Caricamento in corso…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="py-16 text-center text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-2xl" data-testid="notizie-admin-empty">
                Nessun articolo in questa categoria.
              </div>
            )}
            {!loading && filtered.length > 0 && (
              <ul className="space-y-3" data-testid="notizie-admin-list">
                {filtered.map((n) => {
                  const blocked = n.compliance_checks?.blocked;
                  const warns = n.compliance_checks?.warnings || 0;
                  return (
                    <li
                      key={n.id}
                      className={`bg-white border rounded-2xl p-4 hover:border-[#163321]/30 transition-colors ${
                        selectedIds.has(n.id) ? "border-emerald-400 bg-emerald-50/30" : "border-[#E5E2DB]"
                      }`}
                      data-testid={`notizia-card-${n.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {tab === "in_review" && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(n.id)}
                            onChange={() => toggleSelect(n.id)}
                            className="mt-1.5 w-4 h-4"
                            aria-label={`Seleziona articolo ${n.titolo_redazionale}`}
                            data-testid={`notizia-checkbox-${n.id}`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge className={`border ${STATO_BADGE[n.stato] || "bg-neutral-100"}`}>{n.stato}</Badge>
                            {blocked && (
                              <Badge className="border bg-rose-50 text-rose-900 border-rose-300">
                                <ShieldAlert className="w-3 h-3 mr-0.5" aria-hidden="true" /> compliance block
                              </Badge>
                            )}
                            {warns > 0 && !blocked && (
                              <Badge className="border bg-amber-50 text-amber-900 border-amber-300">
                                {warns} warn
                              </Badge>
                            )}
                            <span className="text-[11px] text-neutral-500 inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" aria-hidden="true" /> {timeAgo(n.generated_at)}
                            </span>
                          </div>
                          <button
                            onClick={() => openDetail(n)}
                            className="text-left w-full font-medium text-[#0F1F14] hover:text-[#163321] leading-snug"
                            data-testid={`notizia-open-${n.id}`}
                          >
                            {n.titolo_redazionale || n.titolo_originale || "(senza titolo)"}
                          </button>
                          <p className="text-xs text-[#4A5E51] mt-1">
                            {n.fonti?.[0]?.testata || "fonte n.d."}
                            {n.categoria ? ` · ${n.categoria}` : ""}
                            {n.withdrawn_reason ? ` · ritirata: ${n.withdrawn_reason}` : ""}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* MODALE DETTAGLIO */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="notizia-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-heading pr-8">
                  {detail.titolo_redazionale || detail.titolo_originale || "Articolo"}
                </DialogTitle>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <Badge className={`border ${STATO_BADGE[detail.stato]}`}>{detail.stato}</Badge>
                  <span className="text-xs text-neutral-500">
                    generato {timeAgo(detail.generated_at)} · prompt: <code>{detail.prompt_version || "n.d."}</code>
                  </span>
                </div>
              </DialogHeader>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-1">IA grezzo (read-only)</p>
                  <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-3 text-sm font-mono whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="detail-grezzo">
                    {detail.testo_ia_grezzo || "-"}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-1">Testo finale (editabile)</p>
                  <Textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="min-h-[16rem] text-sm"
                    aria-label="Testo finale editoriale"
                    data-testid="detail-edit"
                  />
                  <Button
                    size="sm" variant="outline" className="mt-2"
                    onClick={() => doEdit(detail.id)}
                    disabled={editText === detail.testo_finale}
                    data-testid="detail-save-edit"
                  >
                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Salva modifiche
                  </Button>
                </div>
              </div>

              {/* Compliance checks */}
              {detail.compliance_checks?.results && (
                <div className="mt-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-2">Compliance checks</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {detail.compliance_checks.results.map((r, i) => (
                      <ComplianceBadge key={i} result={r} />
                    ))}
                  </div>
                </div>
              )}

              {/* Fonti */}
              {(detail.fonti || []).length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-1">Fonti</p>
                  <ul className="space-y-1">
                    {detail.fonti.map((f, i) => (
                      <li key={i} className="text-xs text-[#4A5E51]">
                        <b>{f.testata || "n.d."}</b>
                        {f.autore ? ` — ${f.autore}` : ""}
                        {f.titolo ? `, "${f.titolo}"` : ""}
                        {f.data_pubblicazione ? `, pubblicato ${f.data_pubblicazione.slice(0, 10)}` : ""}
                        {f.url && (
                          <>
                            {" · "}
                            <a href={f.url} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">
                              apri <ExternalLink className="w-3 h-3" aria-hidden="true" />
                            </a>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
                {detail.stato === "in_review" && (
                  <>
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        placeholder="Motivo del rigetto (obbligatorio)…"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        aria-label="Motivo del rigetto"
                        data-testid="detail-reject-reason"
                      />
                      <Button
                        variant="destructive" size="sm"
                        onClick={() => doReject(detail.id)}
                        data-testid="detail-reject-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Rigetta
                      </Button>
                    </div>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => doApprove(detail.id)}
                      disabled={complianceBlocked}
                      title={complianceBlocked ? "Compliance bloccante — correggi prima di approvare" : ""}
                      data-testid="detail-approve-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approva e pubblica
                    </Button>
                  </>
                )}
                {detail.stato === "published" && (
                  <div className="flex-1 flex items-center gap-2">
                    <Input
                      placeholder="Motivo del ritiro (obbligatorio)…"
                      value={withdrawReason}
                      onChange={(e) => setWithdrawReason(e.target.value)}
                      aria-label="Motivo del ritiro"
                      data-testid="detail-withdraw-reason"
                    />
                    <Button
                      variant="outline" size="sm"
                      onClick={() => doWithdraw(detail.id)}
                      data-testid="detail-withdraw-btn"
                    >
                      <Undo2 className="w-4 h-4 mr-1" /> Ritira
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
